// Pure helpers behind the homepage's "Who's moving in <your city>" hero, the
// signed-in "Your co-signs this week" row and "Trending across the US".
// Everything reads the shared /api/artists board (hooks/useBoardData.js), so
// Motion and % change match the leaderboard's (artistsTable/boardModel.js).
import {
  CITY_COORDS, CITY_STATE, CITY_DISPLAY_NAME, LEADERBOARD_CITY_PROXIMITY,
  nearestByDistance, resolveCoords, homeCityKey, normalizeState, stateName,
} from '../usPlaces';
import {
  parseArtistHeading, getFilteredDataForTimeline, resolveLocation, formatLocation,
  bookmarkedAtMillis,
} from '../artistsTable/artistsTableUtils';
import { displayName, nguageValues, validNguage, timelineChange } from '../artistsTable/boardModel';
import { strHash } from '../hashUtils';

// How many artists the hero lists, and the fewest a city needs before the
// hero shows that city on its own rather than widening.
export const SCENE_SIZE = 5;

// A city's scene is every artist within ~0.5° of it (the same distance the
// heat map uses for its city boards), so New York takes in Brooklyn and the
// Bronx and Detroit takes in Inkster and Pontiac.
const SCENE_RADIUS_SQ = LEADERBOARD_CITY_PROXIMITY;

// The browser's time zone -> a tracked city, for a first guess at where the
// viewer is without asking. Coarse on purpose (all of Eastern time reads as
// New York); the chip says which city it picked and "Change" fixes it.
export const TIME_ZONE_CITY = {
  'America/New_York': 'NEW YORK',
  'America/Detroit': 'DETROIT',
  'America/Chicago': 'CHICAGO',
  'America/Denver': 'DENVER',
  'America/Phoenix': 'PHOENIX',
  'America/Los_Angeles': 'LOS ANGELES',
  'America/Boise': 'BOISE',
  'America/Anchorage': 'ANCHORAGE',
  'Pacific/Honolulu': 'HONOLULU',
  'America/Menominee': 'GREEN BAY',
  'US/Eastern': 'NEW YORK',
  'US/Michigan': 'DETROIT',
  'US/Central': 'CHICAGO',
  'US/Mountain': 'DENVER',
  'US/Arizona': 'PHOENIX',
  'US/Pacific': 'LOS ANGELES',
  'US/Alaska': 'ANCHORAGE',
  'US/Hawaii': 'HONOLULU',
};
const TIME_ZONE_PREFIX_CITY = [
  ['America/Indiana/', 'INDIANAPOLIS'],
  ['America/Kentucky/', 'LOUISVILLE'],
  ['America/North_Dakota/', 'FARGO'],
  ['America/Juneau', 'ANCHORAGE'],
  ['America/Sitka', 'ANCHORAGE'],
  ['America/Nome', 'ANCHORAGE'],
  ['America/Yakutat', 'ANCHORAGE'],
  ['America/Metlakatla', 'ANCHORAGE'],
];

// null outside the US (or with no time zone): the hero then opens on the
// biggest scene instead.
export function cityForTimeZone(timeZone) {
  if (!timeZone) return null;
  if (TIME_ZONE_CITY[timeZone]) return TIME_ZONE_CITY[timeZone];
  const hit = TIME_ZONE_PREFIX_CITY.find(([prefix]) => timeZone.startsWith(prefix));
  return hit ? hit[1] : null;
}

// The tracked city nearest a point (the viewer's exact location). Any
// distance: someone two hours from the nearest city still gets that city,
// and the thin-city rule below takes it from there.
export function nearestCity(lat, lng) {
  return nearestByDistance({ lng, lat }, CITY_COORDS).key;
}

export function cityName(key) {
  return CITY_DISPLAY_NAME[key] || key || '';
}

// "Detroit, MI"
export function cityLabel(key) {
  const st = CITY_STATE[key];
  return st ? `${cityName(key)}, ${st}` : cityName(key);
}

// Squared distance in degrees, longitude scaled by latitude so it's roughly
// even in every direction.
function dist2(lng1, lat1, lng2, lat2) {
  const dx = (lng1 - lng2) * Math.cos((((lat1 + lat2) / 2) * Math.PI) / 180);
  const dy = lat1 - lat2;
  return dx * dx + dy * dy;
}

// Every board artist we can put on the map: { heading, key, lng, lat,
// cityKey, state }. cityKey is the tracked city they belong to (heat map
// rule), state their US state from that city or, failing that, their record.
export function placeArtists(artistNames, artistsCollection) {
  const out = [];
  for (const heading of artistNames || []) {
    const { key } = parseArtistHeading(heading);
    const profile = artistsCollection?.[key];
    if (!profile) continue;
    const coords = resolveCoords(profile);
    if (!coords) continue;
    const cityKey = homeCityKey(profile);
    out.push({
      heading,
      key,
      lng: coords[0],
      lat: coords[1],
      cityKey,
      state: (cityKey && CITY_STATE[cityKey]) || normalizeState(profile.state || profile.home_state),
    });
  }
  return out;
}

// Every placed artist in `cityKey`'s scene (within ~0.5° of it). Also the
// leaderboard's city filter, so a city means the same artists on both pages.
export function inScene(placed, cityKey) {
  const c = CITY_COORDS[cityKey];
  if (!c) return [];
  return placed.filter((a) => dist2(a.lng, a.lat, c[0], c[1]) <= SCENE_RADIUS_SQ);
}

// One artist's row for the hero, co-signs or trending: current Motion (the
// latest NGauge on the board) and this week's change, the same like-for-like
// 1W change the leaderboard shows.
export function motionRow(heading, board) {
  const { key } = parseArtistHeading(heading);
  const byDate = board.nguageByArtist?.[key];
  const month = validNguage(nguageValues(byDate, getFilteredDataForTimeline(board.allDateTimes, '1M')));
  if (month.length === 0) return null;
  const profile = board.artistsCollection?.[key] || null;
  const week = validNguage(nguageValues(byDate, getFilteredDataForTimeline(board.allDateTimes, '1W')));
  const { town, region } = resolveLocation(profile);
  return {
    artistKey: key,
    artistName: displayName(board.artistsCollection, heading),
    photoURL: profile?.photoURL || null,
    place: formatLocation(town, region),
    town: town === 'N/A' ? '' : town,
    motion: month[month.length - 1],
    weekPct: timelineChange(profile, week, '1W').percentNum,
    profile,
  };
}

function topRows(headings, board, n = SCENE_SIZE) {
  return headings
    .map((h) => motionRow(h, board))
    .filter(Boolean)
    .sort((a, b) => b.motion - a.motion)
    .slice(0, n);
}

// Canonical tracked cities only: aliases ("NYC", "ST LOUIS", "DMV") share
// their city's coordinates and come after it in CITY_COORDS.
const CANONICAL_CITIES = (() => {
  const seen = new Set();
  return Object.keys(CITY_COORDS).filter((k) => {
    const id = CITY_COORDS[k].join(',');
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
})();

// What the hero shows for a viewer in `cityKey`:
// - the city's top 5 by Motion, when it has at least 5 artists;
// - else the top 5 across its state, if the state has 5;
// - else the nearest city that has 5, with the viewer's own city marked;
// - else (a board too thin for any of that) the top 5 nationally.
// Returns { scope, cityKey, title, note, rows, focus, query }: focus is the
// points the globe should frame, query the leaderboard filter for "See the
// full board" (city=DETROIT or state=MI).
export function sceneFor(cityKey, placed, board) {
  const viewer = CITY_COORDS[cityKey];
  const own = inScene(placed, cityKey);
  const ownName = cityName(cityKey);
  const pts = (rows) => rows
    .map((r) => placed.find((p) => p.key === r.artistKey))
    .filter(Boolean)
    .map((p) => [p.lng, p.lat]);

  if (own.length >= SCENE_SIZE) {
    const rows = topRows(own.map((a) => a.heading), board);
    return { scope: 'city', cityKey, title: ownName, note: '', rows, focus: pts(rows), query: `city=${encodeURIComponent(cityKey)}` };
  }

  const few = `Only ${own.length} artist${own.length === 1 ? '' : 's'} in ${ownName} so far`;
  const st = CITY_STATE[cityKey];
  const statewide = st ? placed.filter((a) => a.state === st) : [];
  if (statewide.length >= SCENE_SIZE) {
    const rows = topRows(statewide.map((a) => a.heading), board);
    const name = stateName(st);
    return {
      scope: 'state', cityKey, title: name,
      note: `${few}, so here are the top artists across ${name}.`,
      rows, focus: pts(rows), query: `state=${st}`,
    };
  }

  let best = null;
  let bestD = Infinity;
  if (viewer) {
    for (const k of CANONICAL_CITIES) {
      if (k === cityKey) continue;
      const c = CITY_COORDS[k];
      const d = dist2(c[0], c[1], viewer[0], viewer[1]);
      if (d < bestD && inScene(placed, k).length >= SCENE_SIZE) { best = k; bestD = d; }
    }
  }
  if (best) {
    // A suburb's circle can take in the big city next door; name the city.
    const hub = hubCity(best, placed);
    if (inScene(placed, hub).length >= SCENE_SIZE) best = hub;
    const rows = topRows(inScene(placed, best).map((a) => a.heading), board);
    return {
      scope: 'nearest', cityKey, title: cityName(best),
      note: `${few}. Here is the closest big scene, ${cityName(best)}.`,
      rows, focus: [...pts(rows), viewer], query: `city=${encodeURIComponent(best)}`,
    };
  }

  const rows = topRows(board.artistNames || [], board);
  return { scope: 'country', cityKey, title: 'the US', note: '', rows, focus: pts(rows), query: '' };
}

// Every placed artist as a globe pin, sized by their latest Motion. Most
// artists are geocoded to their city's centre, so each pin is nudged by a
// fixed, per-artist amount (up to ~0.2°) to keep a city from drawing as one
// dot; the same artist always lands in the same spot.
export function globePins(placed, board) {
  return placed.map((a) => {
    const byDate = board.nguageByArtist?.[a.key] || {};
    const dates = Object.keys(byDate).sort();
    const motion = dates.length ? Number(byDate[dates[dates.length - 1]]) : 0;
    const angle = ((strHash(`${a.key}|a`) % 3600) / 3600) * 2 * Math.PI;
    const r = 0.04 + ((strHash(`${a.key}|r`) % 1000) / 1000) * 0.16;
    return {
      key: a.key,
      lng: a.lng + (r * Math.cos(angle)) / Math.max(0.3, Math.cos((a.lat * Math.PI) / 180)),
      lat: a.lat + r * Math.sin(angle),
      motion: Number.isFinite(motion) ? motion : 0,
    };
  });
}

// The main city of the metro `cityKey` is in: the biggest tracked city
// (CITY_COORDS lists the largest first) within a scene's radius of it whose
// scene has any artists. A shared exact location in Inkster shows Detroit,
// artists geocoded to Brooklyn, Newark or the Bronx all count toward New
// York, and Ypsilanti counts toward Detroit. (It used to be the city whose
// circle held the most artists, which named Detroit's metro after
// Ypsilanti: its circle reaches both Detroit and Ann Arbor.)
export function hubCity(cityKey, placed) {
  const c = CITY_COORDS[cityKey];
  if (!c) return cityKey;
  for (const k of CANONICAL_CITIES) {
    const kc = CITY_COORDS[k];
    if (k !== cityKey && dist2(kc[0], kc[1], c[0], c[1]) > SCENE_RADIUS_SQ) continue;
    if (inScene(placed, k).length > 0) return k;
  }
  return cityKey;
}

// The city with the most artists: where the hero opens when the time zone
// says nothing useful (outside the US).
export function biggestCity(placed) {
  const counts = cityCounts(placed);
  let best = null;
  for (const [k, n] of counts) if (!best || n > best[1]) best = [k, n];
  return best ? best[0] : 'NEW YORK';
}

function cityCounts(placed) {
  const counts = new Map();
  for (const a of placed) if (a.cityKey) counts.set(a.cityKey, (counts.get(a.cityKey) || 0) + 1);
  return counts;
}

// The city picker's chips: the cities with the most artists, plus the one
// showing now if it isn't among them. Suburbs and boroughs count toward
// their metro's main city (Brooklyn toward New York), so the chips are
// distinct scenes.
export function cityChoices(placed, currentKey, n = 8) {
  const byHub = new Map();
  for (const [k, count] of cityCounts(placed)) {
    const hub = hubCity(k, placed);
    byHub.set(hub, (byHub.get(hub) || 0) + count);
  }
  const keys = Array.from(byHub.entries())
    .sort((a, b) => b[1] - a[1] || cityName(a[0]).localeCompare(cityName(b[0])))
    .slice(0, n)
    .map(([k]) => k);
  if (currentKey && !keys.includes(currentKey)) keys.unshift(currentKey);
  return keys.map((key) => ({ key, label: cityName(key) }));
}

// "2026_10_19" for a co-sign date, matching nguage_history's date keys.
function dayKey(ms) {
  const d = new Date(ms);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getUTCFullYear()}_${p(d.getUTCMonth() + 1)}_${p(d.getUTCDate())}`;
}

// The viewer's co-signs that are on the board, each with this week's change
// and its change since they co-signed (Motion on the co-sign day, or the
// first day the board has after it - fromMotion - against today's). Biggest
// mover this week first. up = how many moved up this week.
export function cosignRows(bookmarks, board) {
  const headingByKey = new Map((board.artistNames || []).map((h) => [parseArtistHeading(h).key, h]));
  const rows = [];
  for (const b of bookmarks || []) {
    const heading = headingByKey.get(b?.artistKey);
    if (!heading) continue;
    const row = motionRow(heading, board);
    if (!row) continue;
    const byDate = board.nguageByArtist?.[b.artistKey] || {};
    const dates = Object.keys(byDate).sort();
    const at = bookmarkedAtMillis(b);
    let sincePct = null;
    let fromMotion = null;
    if (at && dates.length > 0) {
      const day = dayKey(at);
      const before = dates.filter((d) => d <= day);
      const from = Number(byDate[before.length ? before[before.length - 1] : dates.find((d) => d > day)]);
      const to = Number(byDate[dates[dates.length - 1]]);
      if (Number.isFinite(from)) fromMotion = from;
      if (from > 0 && Number.isFinite(to)) sincePct = ((to - from) / from) * 100;
    }
    rows.push({ ...row, sincePct, fromMotion });
  }
  rows.sort((a, b) => (b.weekPct ?? -Infinity) - (a.weekPct ?? -Infinity));
  return { rows, up: rows.filter((r) => r.weekPct > 0).length };
}

// "Trending across the US this week": the board's weekly gainers (biggest %
// gain first, see computeWeeklyGainers) with their current Motion.
export function trendingRows(board, n = 5) {
  const headingByKey = new Map((board.artistNames || []).map((h) => [parseArtistHeading(h).key, h]));
  return (board.weeklyGainers || []).slice(0, n).map((g) => {
    const row = motionRow(headingByKey.get(g.artistKey) || `${g.artistName} (${g.artistKey})`, board);
    return {
      artistKey: g.artistKey,
      artistName: g.artistName,
      photoURL: g.photoURL,
      place: row?.place || (g.homeTown !== 'N/A' ? g.homeTown : ''),
      motion: row ? row.motion : null,
      weekPct: g.percentNum,
    };
  });
}

// "▲ 38.0%" / "▼ 3.0%": the arrow, so colour is never the only cue.
export function formatPct(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '';
  const r = Math.round(pct * 10) / 10;
  if (r === 0) return '0.0%';
  return `${r > 0 ? '▲' : '▼'} ${Math.abs(r).toFixed(1)}%`;
}

export function pctTone(pct) {
  const r = Math.round((pct || 0) * 10) / 10;
  return r > 0 ? 'up' : r < 0 ? 'down' : 'flat';
}
